import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { amountToInputText, evaluateAmountInput } from './supportAmount';
import type { SupportRequestFormData } from './schemas/supportRequestFormSchema';
import styles from '@/pages/SupportRequests/NewSupportRequest.module.scss';

interface SupportAmountInputProps {
  id: string;
  label?: string;
  amount: number;
  onAmountChange: (amount: number) => void;
  placeholder: string;
  className?: string;
}

const SupportAmountInput = ({ id, label, amount, onAmountChange, placeholder, className }: SupportAmountInputProps) => {
  const {
    setValue,
    clearErrors,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<SupportRequestFormData>();

  const amountRawInput = watch('amountRawInput');

  useEffect(() => {
    const raw = getValues('amountRawInput');
    const invalid = getValues('amountInputInvalid');
    if ((raw === null || raw === '') && getValues('amount') === 0 && invalid) {
      setValue('amountInputInvalid', false, { shouldValidate: false });
    }
  }, [getValues, setValue]);

  const displayValue = amountToInputText(amount, amountRawInput);
  const parsedFromRaw = amountRawInput !== null && amountRawInput !== '' ? evaluateAmountInput(amountRawInput) : null;
  const fieldError = parsedFromRaw?.error ?? null;
  const formAmountMessage = errors.amount?.message;
  const displayError = fieldError ?? (typeof formAmountMessage === 'string' ? formAmountMessage : null);

  const handleChange = (raw: string) => {
    const result = evaluateAmountInput(raw);
    setValue('amountRawInput', result.displayText === '' ? '' : result.displayText, { shouldValidate: false });
    setValue('amountInputInvalid', Boolean(result.error), { shouldValidate: false });
    if (result.error) {
      clearErrors('amount');
    } else {
      clearErrors('amount');
    }
    onAmountChange(result.amount);
  };

  const input = (
    <>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        dir="ltr"
        autoComplete="off"
        aria-invalid={displayError ? true : undefined}
        aria-describedby={displayError ? `${id}-amount-error` : undefined}
        className={`${styles.supportAmountInput} body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full ${className ?? ''} ${displayError ? 'border-(--border-primary)' : ''}`}
        value={displayValue}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => {
          if (fieldError) return;
          setValue('amountRawInput', null, { shouldValidate: false });
        }}
        onWheel={(e) => e.currentTarget.blur()}
        placeholder={placeholder}
      />
      {displayError && (
        <p id={`${id}-amount-error`} role="alert" className="body-sm-regular text-(--font-primary) mt-1 text-end">
          {displayError}
        </p>
      )}
    </>
  );

  if (!label) {
    return input;
  }

  return (
    <div>
      <label htmlFor={id} className="body-sm-regular text-(--font-heading) block mb-1.5">
        {label}
      </label>
      {input}
    </div>
  );
};

export default SupportAmountInput;
